import db from './db.js';
import Storage from './storage.js';
import Upload from './upload.js';
import imageHelper from './imageHelper.js';
import events from './event.js';
import Logger from './logger.js';

/**
 * Upload control of the [mscp_shop] page.
 *
 * The visitor picks an image on the shop page itself. It goes through the same machinery as the
 * popup (Upload.saveImage -> db.save -> uploadHandler chunked upload), the cards then show the
 * image in a neutral framed render, and a click on a card opens the popup past the upload step.
 *
 * States (data-state on .mscp-shop-upload):
 *   none    no image; cards keep their static example pictures
 *   pending image chosen, upload not finished (or HEIC being converted); static pictures stay
 *   ready   uploaded; previews shown, cards skip the upload step
 *   failed  upload failed (or the page was reloaded mid-upload); static pictures stay
 */

// Image keys that belong to the uploaded image (not to the design choices around it).
const IMAGE_KEYS = ['activeImageID', 'lastUploadedImageURL', 'orientation', 'cropBox'];

const MESSAGES = {
    invalidFileType: 'Filen är inte en bild vi kan hantera. Välj en JPG- eller PNG-bild.',
    fileTooLarge: 'Bilden är för stor.',
    saveFailed: 'Bilden kunde inte läsas. Försök med en annan bild.',
    uploadFailed: 'Uppladdningen misslyckades. Försök igen.',
};

/**
 * The active image when it can be used for previews and for skipping the upload step:
 * uploaded, with a server URL. Anything else (none, pending, converting, failed) is null.
 * @returns {Promise<{url: string, image: Object} | null>}
 */
export async function getReadyImage() {
    const active = await imageHelper.getActiveImage();
    if (!active || active.image?.uploadStatus !== 'uploaded' || !active.image?.serverUrl) return null;
    return { url: active.image.serverUrl, image: active.image };
}

class ShopUpload {
    root = null;
    upload = null;
    errorMessage = '';

    init() {
        this.root = document.querySelector('.mscp-shop-upload');
        if (!this.root) return;

        // The shop page has no popup that would start the session; db.save needs one.
        if (!Storage.getCurrentSessionId()) {
            Storage.startSession();
        }

        this.upload = new Upload(this.root, false, {
            standalone: true,
            onError: (type) => this.showError(MESSAGES[type] || MESSAGES.saveFailed),
        });

        this.root.querySelectorAll('[data-mscp-action]').forEach((el) => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                const action = el.dataset.mscpAction;
                if (action === 'upload' || action === 'replace') {
                    this.showError('');
                    this.upload.triggerChange();
                } else if (action === 'remove') {
                    this.remove();
                }
            });
        });

        // Drag and drop onto the control, as on the popup dropzone.
        this.root.addEventListener('dragover', (e) => {
            e.preventDefault();
            this.root.classList.add('mscp-shop-upload--dragover');
        });
        this.root.addEventListener('dragleave', () => this.root.classList.remove('mscp-shop-upload--dragover'));
        this.root.addEventListener('drop', async (e) => {
            e.preventDefault();
            this.root.classList.remove('mscp-shop-upload--dragover');
            if (e.dataTransfer?.files?.length) {
                this.showError('');
                await this.upload.saveImage(e.dataTransfer.files);
                this.refresh();
            }
        });

        // The file input inside Upload saves by itself; here we only follow the state.
        events.add('newImageLocalSaved', () => this.refresh());
        events.add('imageUploadStatusChanged', () => this.refresh());
        events.add('imageUploadFailed', () => {
            this.showError(MESSAGES.uploadFailed);
            this.refresh();
        });

        this.root.hidden = false; // rendered hidden by PHP: without JS the control would do nothing
        this.refresh();
    }

    showError(message) {
        this.errorMessage = typeof message === 'string' ? message : '';
        const el = this.root?.querySelector('.mscp-shop-upload__error');
        if (el) {
            el.textContent = this.errorMessage;
            el.hidden = !this.errorMessage;
        }
    }

    /**
     * Clear the stored image: local metadata and memory, the image keys of the session, and persist.
     */
    async remove() {
        await db.clearAllImages();
        IMAGE_KEYS.forEach((key) => Storage.remove(key));
        Storage.commitSession();
        this.showError('');
        this.refresh();
    }

    async getState() {
        const active = await imageHelper.getActiveImage();
        if (!active) return { state: 'none', url: null };
        const status = active.image?.uploadStatus;
        if (status === 'uploaded' && active.image?.serverUrl) return { state: 'ready', url: active.image.serverUrl };
        if (status === 'failed') return { state: 'failed', url: null };
        // 'pending', 'converting' (HEIC without blob support) and anything unknown
        return { state: 'pending', url: null };
    }

    async refresh() {
        if (!this.root) return;
        const { state, url } = await this.getState();
        this.root.dataset.state = state;
        if (state === 'failed' && !this.errorMessage) {
            this.showError(MESSAGES.uploadFailed);
        }
        if (state === 'ready') {
            this.showError('');
        }
        this.applyThumb(state === 'ready' ? url : null);
        this.applyPreviews(state === 'ready' ? url : null);
    }

    /** The small picture of the visitor image at the left of the control (sketch 4c). */
    applyThumb(url) {
        const thumb = this.root.querySelector('.mscp-shop-upload__thumb');
        if (!thumb) return;
        if (url) {
            thumb.src = url;
            thumb.hidden = false;
        } else {
            thumb.removeAttribute('src');
            thumb.hidden = true;
        }
    }

    /**
     * Show the visitor image in the picture window of every card that holds one image, or restore the
     * example pictures when url is null. Fotobås and Kontaktkarta need several images and keep their
     * example (and the EXEMPELBILD band). Everything is client side: the server HTML is identical for
     * every visitor (page caches).
     */
    applyPreviews(url) {
        document.querySelectorAll('.mscp-shop__card[data-look][data-own="1"]').forEach((card) => {
            const img = card.querySelector('.mscp-shop__ex');
            if (!img) return;
            if (!img.dataset.mscpOrig) img.dataset.mscpOrig = img.getAttribute('src') || '';
            if (!url) {
                img.src = img.dataset.mscpOrig;
                card.classList.remove('mscp-shop__card--user');
                return;
            }
            // A picture the browser cannot show (e.g. an unconverted HEIC) must not leave an empty
            // window: this card falls back to its example.
            img.onerror = () => {
                Logger.warn('Shop preview failed to load, keeping the example picture.');
                img.onerror = null;
                img.src = img.dataset.mscpOrig;
                card.classList.remove('mscp-shop__card--user');
            };
            img.src = url;
            card.classList.add('mscp-shop__card--user');
        });
    }
}

const shopUpload = new ShopUpload();
export default shopUpload;
